import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Message } from '@/models/message'
import api from '@/shared/api'
import { ENDPOINTS, MESSAGE_STATUS, MESSAGE_TYPES, UTILS } from '@/shared/constants'
import { getMessageAliases, getMessageKey } from '@/shared/messageIdentity'
import { areStructurallyEqual } from '@/shared/structuralEquality'
import { RootState } from '..'

const DEFAULT_PAGE_SIZE = 50
const RECENT_MESSAGES_CHECK_MIN_INTERVAL_MS = 30_000

interface RecentMessageRevision {
  id: string
  updatedAt: string
}

export interface ConversationMessagesPageInfo {
  hasOlder: boolean
  hasNewer: boolean
  oldestCursor?: string
  newestCursor?: string
}

interface ConversationMessageBucket {
  entities: Record<string, Message>
  ids: string[]
  keyByMessageAlias: Record<string, string>
  pageInfo: ConversationMessagesPageInfo
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  olderStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  newerStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  aroundStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  reconciliationStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  lastReconciliationAttemptAt?: number
  anchorMessageId?: string
  highlightedMessageId?: string
  error?: string
}

interface ConversationMessagesState {
  byConversationId: Record<number, ConversationMessageBucket>
}

interface MessagesPageResponse {
  items: Message[]
  pageInfo?: Partial<ConversationMessagesPageInfo>
}

interface FetchMessagesArgs {
  conversationId: number
  limit?: number
  cursor?: string
  messageId?: string
  silent?: boolean
}

const initialState: ConversationMessagesState = {
  byConversationId: {},
}

function getMessageTimestamp(message: Message): number {
  return new Date(message.sentAt || message.createdAt).getTime()
}

function sortMessageIds(entities: Record<string, Message>) {
  return Object.keys(entities).sort((a, b) => {
    const first = entities[a]
    const second = entities[b]
    const timeDiff = getMessageTimestamp(first) - getMessageTimestamp(second)

    if (timeDiff !== 0) return timeDiff
    return getMessageKey(first).localeCompare(getMessageKey(second))
  })
}

function indexMessageAliases(
  bucket: ConversationMessageBucket,
  key: string,
  message: Message
) {
  getMessageAliases(message).forEach((alias) => {
    bucket.keyByMessageAlias[alias] = key
  })
}

function rebuildMessageAliasIndex(bucket: ConversationMessageBucket) {
  bucket.keyByMessageAlias = {}

  Object.entries(bucket.entities).forEach(([key, message]) => {
    indexMessageAliases(bucket, key, message)
  })
}

function ensureMessageAliasIndex(bucket: ConversationMessageBucket) {
  if (bucket.keyByMessageAlias) return

  rebuildMessageAliasIndex(bucket)
}

function getBucket(
  state: ConversationMessagesState,
  conversationId: number
): ConversationMessageBucket {
  if (!state.byConversationId[conversationId]) {
    state.byConversationId[conversationId] = {
      entities: {},
      ids: [],
      keyByMessageAlias: {},
      pageInfo: {
        hasOlder: true,
        hasNewer: false,
      },
      status: 'idle',
      olderStatus: 'idle',
      newerStatus: 'idle',
      aroundStatus: 'idle',
      reconciliationStatus: 'idle',
    }
  }

  const bucket = state.byConversationId[conversationId]
  ensureMessageAliasIndex(bucket)

  return bucket
}

function mergeMessage(currentMessage: Message | undefined, message: Message) {
  if (!currentMessage) return message

  const currentParent = currentMessage.parentMessage
  const incomingParent = message.parentMessage
  const shouldKeepCurrentParentContent =
    currentParent?.type === MESSAGE_TYPES.FILE &&
    incomingParent?.type === MESSAGE_TYPES.FILE &&
    Boolean(currentParent.content) &&
    (!incomingParent.content || incomingParent.content.includes('whatsapp.net'))
  const nextContent = message.content ?? currentMessage.content
  const shouldKeepCurrentLinkPreview =
    message.linkPreview === undefined ||
    (message.linkPreview === null &&
      Boolean(currentMessage.linkPreview) &&
      nextContent === currentMessage.content)

  const mergedMessage = {
    ...currentMessage,
    ...message,
    user: message.user ?? currentMessage.user,
    conversation: message.conversation ?? currentMessage.conversation,
    parentMessage: incomingParent
      ? {
          ...currentParent,
          ...incomingParent,
          content: shouldKeepCurrentParentContent
            ? currentParent?.content
            : incomingParent.content ?? currentParent?.content,
      }
      : currentParent,
    content: nextContent,
    linkPreview: shouldKeepCurrentLinkPreview
      ? currentMessage.linkPreview
      : message.linkPreview,
  }

  return areStructurallyEqual(currentMessage, mergedMessage)
    ? currentMessage
    : mergedMessage
}

function areMessageIdsEqual(first: string[], second: string[]) {
  return (
    first.length === second.length &&
    first.every((id, index) => id === second[index])
  )
}

function findMessageKey(
  bucket: ConversationMessageBucket,
  incomingMessage: Message
) {
  ensureMessageAliasIndex(bucket)

  return getMessageAliases(incomingMessage)
    .map((alias) => bucket.keyByMessageAlias[alias])
    .find((key) => key && bucket.entities[key])
}

function removeMessageAliases(
  bucket: ConversationMessageBucket,
  message: Message
) {
  getMessageAliases(message).forEach((alias) => {
    delete bucket.keyByMessageAlias[alias]
  })
}

function mergeMessages(
  bucket: ConversationMessageBucket,
  messages: Message[],
  pageInfo?: Partial<ConversationMessagesPageInfo>
) {
  messages.forEach((message) => {
    const incomingKey = getMessageKey(message)
    const existingKey = findMessageKey(bucket, message)
    const currentMessage = existingKey
      ? bucket.entities[existingKey]
      : undefined
    const nextMessage = mergeMessage(currentMessage, message)

    if (currentMessage === nextMessage && existingKey === incomingKey) {
      return
    }

    if (currentMessage) {
      removeMessageAliases(bucket, currentMessage)
    }

    if (existingKey && existingKey !== incomingKey) {
      delete bucket.entities[existingKey]
    }

    bucket.entities[incomingKey] = nextMessage
    indexMessageAliases(bucket, incomingKey, nextMessage)
  })

  const sortedIds = sortMessageIds(bucket.entities)
  if (!areMessageIdsEqual(bucket.ids, sortedIds)) {
    bucket.ids = sortedIds
  }

  const nextPageInfo = {
    ...bucket.pageInfo,
    ...pageInfo,
  }
  if (!areStructurallyEqual(bucket.pageInfo, nextPageInfo)) {
    bucket.pageInfo = nextPageInfo
  }
}

function getMessagesRoute(conversationId: number) {
  return ENDPOINTS.CONVERSATIONS.GET.MESSAGES_BY_ID.replace(
    UTILS.PARAMS.ID,
    conversationId.toString()
  )
}

function getMessageRevisionsRoute(conversationId: number) {
  return ENDPOINTS.CONVERSATIONS.GET.MESSAGE_REVISIONS_BY_ID.replace(
    UTILS.PARAMS.ID,
    conversationId.toString()
  )
}

function getAroundMessagesRoute(conversationId: number, messageId: string) {
  return ENDPOINTS.CONVERSATIONS.GET.MESSAGES_AROUND_BY_ID.replace(
    UTILS.PARAMS.ID,
    conversationId.toString()
  ).replace(UTILS.PARAMS.MESSAGE_ID, messageId)
}

function normalizeMessagesResponse(data: Message[] | MessagesPageResponse) {
  if (Array.isArray(data)) {
    return {
      items: data,
      pageInfo: {
        hasOlder: false,
        hasNewer: false,
      },
    }
  }

  return {
    items: data.items ?? [],
    pageInfo: {
      hasOlder: data.pageInfo?.hasOlder ?? false,
      hasNewer: data.pageInfo?.hasNewer ?? false,
      oldestCursor: data.pageInfo?.oldestCursor,
      newestCursor: data.pageInfo?.newestCursor,
    },
  }
}

export const fetchLatestConversationMessages = createAsyncThunk(
  'conversationMessages/fetchLatest',
  async ({ conversationId, limit = DEFAULT_PAGE_SIZE }: FetchMessagesArgs) => {
    const response = await api.get(getMessagesRoute(conversationId), {
      params: { limit },
    })

    return {
      conversationId,
      ...normalizeMessagesResponse(response.data),
    }
  }
)

export const reconcileRecentConversationMessages = createAsyncThunk(
  'conversationMessages/reconcileRecent',
  async ({ conversationId }: { conversationId: number }, { getState, signal }) => {
    const revisionsResponse = await api.get<RecentMessageRevision[]>(
      getMessageRevisionsRoute(conversationId),
      { signal }
    )
    const bucket = (getState() as RootState).conversationMessages.byConversationId[
      conversationId
    ]
    const hasDifference = revisionsResponse.data.some(({ id, updatedAt }) => {
      const messageKey = bucket?.keyByMessageAlias[id]
      const message = messageKey ? bucket?.entities[messageKey] : undefined

      return !message || message.updatedAt !== updatedAt
    })

    if (!hasDifference) {
      return { conversationId, items: [] as Message[], pageInfo: undefined }
    }

    const messagesResponse = await api.get<Message[] | MessagesPageResponse>(
      getMessagesRoute(conversationId),
      { params: { limit: DEFAULT_PAGE_SIZE }, signal }
    )

    return {
      conversationId,
      ...normalizeMessagesResponse(messagesResponse.data),
    }
  },
  {
    condition: ({ conversationId }, { getState }) => {
      const state = getState() as RootState
      const bucket = state.conversationMessages.byConversationId[conversationId]

      if (
        state.conversations.openedConversation?.id !== conversationId ||
        !bucket ||
        bucket.status === 'idle' ||
        bucket.status === 'loading' ||
        bucket.newerStatus === 'loading' ||
        bucket.reconciliationStatus === 'loading'
      ) {
        return false
      }

      return (
        Date.now() - (bucket.lastReconciliationAttemptAt ?? 0) >=
        RECENT_MESSAGES_CHECK_MIN_INTERVAL_MS
      )
    },
  }
)

export const fetchOlderConversationMessages = createAsyncThunk(
  'conversationMessages/fetchOlder',
  async ({
    conversationId,
    cursor,
    limit = DEFAULT_PAGE_SIZE,
  }: FetchMessagesArgs) => {
    const response = await api.get(getMessagesRoute(conversationId), {
      params: { limit, before: cursor },
    })

    return {
      conversationId,
      ...normalizeMessagesResponse(response.data),
    }
  }
)

export const fetchNewerConversationMessages = createAsyncThunk(
  'conversationMessages/fetchNewer',
  async ({
    conversationId,
    cursor,
    limit = DEFAULT_PAGE_SIZE,
  }: FetchMessagesArgs) => {
    const response = await api.get(getMessagesRoute(conversationId), {
      params: { limit, after: cursor },
    })

    return {
      conversationId,
      ...normalizeMessagesResponse(response.data),
    }
  }
)

export const fetchConversationMessagesAround = createAsyncThunk(
  'conversationMessages/fetchAround',
  async ({
    conversationId,
    messageId,
    limit = DEFAULT_PAGE_SIZE,
  }: FetchMessagesArgs) => {
    if (!messageId) {
      throw new Error('messageId is required')
    }

    const response = await api.get(getAroundMessagesRoute(conversationId, messageId), {
      params: {
        limitBefore: Math.floor(limit / 2),
        limitAfter: Math.ceil(limit / 2),
      },
    })

    return {
      conversationId,
      anchorMessageId: messageId,
      ...normalizeMessagesResponse(response.data),
    }
  }
)

const conversationMessagesSlice = createSlice({
  name: 'conversationMessages',
  initialState,
  reducers: {
    seedConversationMessages: (
      state,
      action: PayloadAction<{ conversationId: number; messages: Message[] }>
    ) => {
      const bucket = getBucket(state, action.payload.conversationId)
      mergeMessages(bucket, action.payload.messages, {
        hasOlder: bucket.pageInfo.hasOlder,
        hasNewer: bucket.pageInfo.hasNewer,
      })
    },
    upsertConversationMessage: (state, action: PayloadAction<Message>) => {
      const bucket = getBucket(state, action.payload.conversationId)
      mergeMessages(bucket, [action.payload])
    },
    removeOptimisticConversationMessage: (
      state,
      action: PayloadAction<{ conversationId: number; clientMessageId: string }>
    ) => {
      const bucket = state.byConversationId[action.payload.conversationId]
      if (!bucket) return

      const messageKey = bucket.keyByMessageAlias[action.payload.clientMessageId]
      const message = messageKey ? bucket.entities[messageKey] : undefined
      if (!message || message.status !== MESSAGE_STATUS.PENDING) return

      removeMessageAliases(bucket, message)
      delete bucket.entities[messageKey]
      bucket.ids = bucket.ids.filter((id) => id !== messageKey)
    },
    updateConversationMessageStatus: (
      state,
      action: PayloadAction<Message>
    ) => {
      const bucket = state.byConversationId[action.payload.conversationId]
      if (!bucket) return

      const messageKey = findMessageKey(bucket, action.payload)
      const message = messageKey ? bucket.entities[messageKey] : undefined
      if (
        message &&
        (message.status !== action.payload.status ||
          message.failureReason !== (action.payload.failureReason ?? null) ||
          message.receivedAt !==
            (action.payload.receivedAt ?? message.receivedAt) ||
          message.readAt !== (action.payload.readAt ?? message.readAt))
      ) {
        message.status = action.payload.status
        message.failureReason = action.payload.failureReason ?? null
        message.receivedAt = action.payload.receivedAt ?? message.receivedAt
        message.readAt = action.payload.readAt ?? message.readAt
      }
    },
    updateConversationMessageId: (state, action: PayloadAction<Message>) => {
      const bucket = state.byConversationId[action.payload.conversationId]
      if (!bucket) return

      const existingKey = findMessageKey(bucket, action.payload)

      if (!existingKey) return

      const message = bucket.entities[existingKey]
      const nextKey = getMessageKey(action.payload)
      // Mantém o bucket intacto quando a confirmação já foi aplicada.
      if (
        message.id === action.payload.id &&
        message.messageId === action.payload.messageId &&
        message.status === action.payload.status &&
        message.sentAt === action.payload.sentAt &&
        existingKey === nextKey
      ) {
        return
      }

      removeMessageAliases(bucket, message)

      message.id = action.payload.id
      message.messageId = action.payload.messageId
      message.status = action.payload.status
      message.sentAt = action.payload.sentAt

      if (existingKey !== nextKey) {
        bucket.entities[nextKey] = message
        delete bucket.entities[existingKey]
      }

      indexMessageAliases(bucket, nextKey, message)
      const sortedIds = sortMessageIds(bucket.entities)
      if (!areMessageIdsEqual(bucket.ids, sortedIds)) {
        bucket.ids = sortedIds
      }
    },
    setConversationHighlightedMessage: (
      state,
      action: PayloadAction<{
        conversationId: number
        messageId?: string
      }>
    ) => {
      const bucket = getBucket(state, action.payload.conversationId)
      bucket.highlightedMessageId = action.payload.messageId
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLatestConversationMessages.pending, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        if (action.meta.arg.silent && bucket.ids.length) {
          bucket.newerStatus = 'loading'
          return
        }

        bucket.status = 'loading'
      })
      .addCase(fetchLatestConversationMessages.fulfilled, (state, action) => {
        const bucket = getBucket(state, action.payload.conversationId)
        bucket.status = 'succeeded'
        if (action.meta.arg.silent) {
          bucket.newerStatus = 'succeeded'
        }
        bucket.error = undefined
        bucket.lastReconciliationAttemptAt = Date.now()
        mergeMessages(bucket, action.payload.items, action.payload.pageInfo)
      })
      .addCase(reconcileRecentConversationMessages.pending, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        bucket.reconciliationStatus = 'loading'
        bucket.lastReconciliationAttemptAt = Date.now()
      })
      .addCase(reconcileRecentConversationMessages.fulfilled, (state, action) => {
        const bucket = getBucket(state, action.payload.conversationId)
        bucket.reconciliationStatus = 'succeeded'
        if (action.payload.items.length) {
          mergeMessages(bucket, action.payload.items, action.payload.pageInfo)
        }
      })
      .addCase(reconcileRecentConversationMessages.rejected, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        bucket.reconciliationStatus = 'failed'
        if (action.meta.aborted) {
          bucket.lastReconciliationAttemptAt = undefined
        }
      })
      .addCase(fetchLatestConversationMessages.rejected, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        if (action.meta.arg.silent && bucket.ids.length) {
          bucket.newerStatus = 'failed'
        } else {
          bucket.status = 'failed'
        }
        bucket.error = action.error.message
      })
      .addCase(fetchOlderConversationMessages.pending, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        bucket.olderStatus = 'loading'
      })
      .addCase(fetchOlderConversationMessages.fulfilled, (state, action) => {
        const bucket = getBucket(state, action.payload.conversationId)
        bucket.olderStatus = 'succeeded'
        bucket.error = undefined
        mergeMessages(bucket, action.payload.items, action.payload.pageInfo)
      })
      .addCase(fetchOlderConversationMessages.rejected, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        bucket.olderStatus = 'failed'
        bucket.error = action.error.message
      })
      .addCase(fetchNewerConversationMessages.pending, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        bucket.newerStatus = 'loading'
      })
      .addCase(fetchNewerConversationMessages.fulfilled, (state, action) => {
        const bucket = getBucket(state, action.payload.conversationId)
        bucket.newerStatus = 'succeeded'
        bucket.error = undefined
        mergeMessages(bucket, action.payload.items, action.payload.pageInfo)
      })
      .addCase(fetchNewerConversationMessages.rejected, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        bucket.newerStatus = 'failed'
        bucket.error = action.error.message
      })
      .addCase(fetchConversationMessagesAround.pending, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        bucket.aroundStatus = 'loading'
      })
      .addCase(fetchConversationMessagesAround.fulfilled, (state, action) => {
        const bucket = getBucket(state, action.payload.conversationId)
        bucket.aroundStatus = 'succeeded'
        bucket.anchorMessageId = action.payload.anchorMessageId
        bucket.error = undefined
        mergeMessages(bucket, action.payload.items, action.payload.pageInfo)
      })
      .addCase(fetchConversationMessagesAround.rejected, (state, action) => {
        const bucket = getBucket(state, action.meta.arg.conversationId)
        bucket.aroundStatus = 'failed'
        bucket.error = action.error.message
      })
  },
})

export const {
  seedConversationMessages,
  removeOptimisticConversationMessage,
  setConversationHighlightedMessage,
  updateConversationMessageId,
  updateConversationMessageStatus,
  upsertConversationMessage,
} = conversationMessagesSlice.actions

export const selectConversationMessagesBucket = (
  state: RootState,
  conversationId: number
) => state.conversationMessages.byConversationId[conversationId]

export const selectConversationMessages = (
  state: RootState,
  conversationId: number
) => {
  const bucket = selectConversationMessagesBucket(state, conversationId)
  if (!bucket) return []

  return bucket.ids.map((id) => bucket.entities[id]).filter(Boolean)
}

export default conversationMessagesSlice.reducer
